import React, { useEffect, useState } from 'react';
import {
    Drawer, Typography, Button, Form, Input, Divider, Alert,
    Upload, InputNumber, Select, Switch, message, Space, Tag,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import TextArea from 'antd/es/input/TextArea';
import ApiService from '../../APIServices/ApiService';

const { Title, Text } = Typography;
const { Option } = Select;

/**
 * Create / edit drawer for a sister-storefront book.
 *
 * One component for both, because the API treats them almost identically: only
 * the fields you send are changed on edit. Splitting it into Add and Edit
 * copies would have duplicated the file-upload handling, which is the fiddly
 * part and the part most likely to drift.
 *
 * Deliberately NOT the travel-guide form: `category` here is the SITE KEY
 * (youflora, cybellium, …), not a Category ObjectId, and there is no
 * address/lat/long — a tax reference has no location.
 */
const OtherBookForm = ({ visible, onClose, book, sites, categories, onSaved }) => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const isEdit = Boolean(book && book._id);

    useEffect(() => {
        if (!visible) return;
        if (isEdit) {
            form.setFieldsValue({
                name: book.name,
                eng_name: book.eng_name,
                description: book.description,
                eng_description: book.eng_description,
                category: book.site || book.category,
                city: book.city,
                country: book.country,
                price: book.price,
                lang: book.lang,
                lang_short: book.lang_short,
                subjectCategory: book.subjectCategory,
                status: book.status !== false,
            });
        } else {
            form.resetFields();
            form.setFieldsValue({ status: true, lang: 'English', lang_short: 'en' });
        }
    }, [visible, book]);

    /** antd hands back either a wrapped event or a fileList depending on usage. */
    const pickFile = (value) => {
        if (!value) return null;
        if (value.file) return value.file.originFileObj || value.file;
        if (Array.isArray(value) && value.length) return value[0].originFileObj || value[0];
        return null;
    };

    const submit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            return;
        }

        const fd = new FormData();
        // Only append what has a value. On edit an untouched field must not be
        // sent as "" — the API reads empty string as "clear this".
        const scalars = ['name', 'eng_name', 'description', 'eng_description',
            'category', 'city', 'country', 'price', 'lang', 'lang_short'];
        scalars.forEach((k) => {
            if (values[k] !== undefined && values[k] !== null && values[k] !== '') {
                fd.append(k, values[k]);
            }
        });
        fd.append('status', values.status ? 'true' : 'false');

        const pdf = pickFile(values.file);
        const epub = pickFile(values.epub);
        const json = pickFile(values.json);
        const image = pickFile(values.image);

        if (pdf) fd.append('file', pdf);
        if (epub) fd.append('epub', epub);
        if (json) fd.append('json', json);
        if (image) fd.append('image', image);

        // The API requires a PDF and a cover to CREATE. Catching it here saves a
        // round trip and a 400 the user has to decode.
        if (!isEdit && (!pdf || !image)) {
            message.error('A PDF and a cover image are required to create a book.');
            return;
        }

        setSaving(true);
        try {
            if (isEdit) {
                await ApiService.updateOtherBook(book._id, fd);
                message.success('Book updated.');
            } else {
                await ApiService.createOtherBook(fd);
                message.success('Book created.');
            }
            onSaved();
            onClose();
        } catch (error) {
            const data = error?.response?.data;
            const missing = data?.missing?.length ? ` (${data.missing.join(', ')})` : '';
            message.error((data?.message || 'Save failed.') + missing);
        } finally {
            setSaving(false);
        }
    };

    const single = { beforeUpload: () => false, maxCount: 1 };

    return (
        <Drawer
            title={<Title level={4} className="my-0">{isEdit ? 'Edit Book' : 'Add Book'}</Title>}
            open={visible}
            onClose={onClose}
            width={520}
            footer={
                <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" loading={saving} onClick={submit} className="custom-primary-btn">
                        {isEdit ? 'Save Changes' : 'Create Book'}
                    </Button>
                </Space>
            }
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    name="category"
                    label="Storefront"
                    rules={[{ required: true, message: 'Pick which storefront this book belongs to' }]}
                >
                    <Select placeholder="Select a storefront" disabled={isEdit}>
                        {(sites || []).map((s) => (
                            <Option key={s.key} value={s.key}>{s.name} ({s.currency})</Option>
                        ))}
                    </Select>
                </Form.Item>
                {isEdit && (
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message="The storefront cannot be changed after creation — the stored files live under that brand's prefix."
                    />
                )}

                {/*
                  The SUBJECT of the book, which is not the storefront above.
                  Optional: left empty, the backend derives it from the English
                  title when the book is saved. Set explicitly, the value sticks —
                  the model only fills in a category that is absent, so a
                  correction made here is not undone by the next save.
                */}
                <Form.Item
                    name="subjectCategory"
                    label="Subject category"
                    extra="Leave empty to derive it from the English title. Set it to override a wrong guess."
                >
                    <Select placeholder="Derive from the title" allowClear showSearch optionFilterProp="children">
                        {(categories || []).map((c) => (
                            <Option key={c.value} value={c.value}>
                                {c.label}{c.total ? ` (${c.total})` : ''}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item name="eng_name" label="Title (English)" rules={[{ required: true }]}>
                    <Input placeholder="The Complete Gardening Guide For Bangladesh" />
                </Form.Item>
                <Form.Item name="name" label="Display title" rules={[{ required: true }]}>
                    <Input placeholder="Shown on the storefront" />
                </Form.Item>

                <Form.Item name="eng_description" label="Description (English)" rules={[{ required: true }]}>
                    <TextArea rows={3} />
                </Form.Item>
                <Form.Item name="description" label="Display description" rules={[{ required: true }]}>
                    <TextArea rows={3} />
                </Form.Item>

                <Space style={{ display: 'flex' }} align="start">
                    <Form.Item name="country" label="Country" style={{ flex: 1 }}>
                        <Input placeholder="Bangladesh" />
                    </Form.Item>
                    <Form.Item name="city" label="City" style={{ flex: 1 }}>
                        <Input placeholder="Optional" />
                    </Form.Item>
                </Space>

                <Space style={{ display: 'flex' }} align="start">
                    <Form.Item name="price" label="Price" rules={[{ required: true }]} style={{ flex: 1 }}>
                        <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="9.99" />
                    </Form.Item>
                    <Form.Item name="lang" label="Language" style={{ flex: 1 }}>
                        <Input placeholder="English" />
                    </Form.Item>
                    <Form.Item name="lang_short" label="Code" style={{ width: 90 }}>
                        <Input placeholder="en" />
                    </Form.Item>
                </Space>

                <Form.Item name="status" label="Published" valuePropName="checked">
                    <Switch />
                </Form.Item>

                <Divider orientation="left" plain>
                    <Text type="secondary">Files</Text>
                </Divider>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    PDF and cover are required to create. EPUB is a shipped format alongside
                    the PDF; JSON is optional.
                    {isEdit && ' Leave a slot empty to keep the existing file.'}
                </Text>

                <Form.Item name="file" label="PDF" style={{ marginTop: 12 }}>
                    <Upload {...single} accept=".pdf">
                        <Button icon={<UploadOutlined />}>Select PDF</Button>
                    </Upload>
                </Form.Item>
                <Form.Item name="epub" label={<span>EPUB <Tag color="blue">recommended</Tag></span>}>
                    <Upload {...single} accept=".epub">
                        <Button icon={<UploadOutlined />}>Select EPUB</Button>
                    </Upload>
                </Form.Item>
                <Form.Item name="json" label={<span>JSON <Tag>optional</Tag></span>}>
                    <Upload {...single} accept=".json">
                        <Button icon={<UploadOutlined />}>Select JSON</Button>
                    </Upload>
                </Form.Item>
                <Form.Item name="image" label="Cover image">
                    <Upload {...single} accept=".png,.jpg,.jpeg,.webp" listType="picture">
                        <Button icon={<UploadOutlined />}>Select cover</Button>
                    </Upload>
                </Form.Item>
            </Form>
        </Drawer>
    );
};

export default OtherBookForm;
